import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { 
  Camera, 
  Calculator, 
  Megaphone, 
  CheckCircle,
  ArrowRight,
  Sparkles,
  Clock,
  TrendingUp,
  Shield,
  Zap
} from "lucide-react";

// Navigation Component
const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "glass py-3" : "bg-transparent py-5"
      }`}
    >
      <div className="container mx-auto px-4 lg:px-8 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <img 
            src="https://www.machinerymarketed.com/app-logo.png" 
            alt="MachineryMarketed Logo" 
            className="h-10 w-10"
          />
          <span className="font-bold text-xl tracking-tight" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
            MACHINERYMARKETED
          </span>
        </Link>
        <Link to="/" className="btn-outline text-sm">Back to Home</Link>
      </div>
    </nav>
  );
};

// Step Card Component
const StepCard = ({ number, title, icon: Icon, color, description, features, image, isReversed }) => (
  <div className={`grid lg:grid-cols-2 gap-12 items-center ${isReversed ? 'lg:flex-row-reverse' : ''} mb-24`}>
    {/* Image Side */}
    <div className={`relative ${isReversed ? 'lg:order-2' : ''}`}>
      <div 
        className="rounded-2xl overflow-hidden"
        style={{ boxShadow: `0 20px 60px ${color}40` }}
      >
        <img 
          src={image} 
          alt={title}
          className="w-full h-80 lg:h-96 object-cover"
          loading="lazy"
        />
      </div>
      {/* Step Number Badge */}
      <div 
        className="absolute -top-6 -left-6 w-20 h-20 rounded-2xl flex items-center justify-center font-bold text-3xl z-10"
        style={{ 
          background: color,
          color: "white",
          boxShadow: `0 15px 40px ${color}60`
        }}
      >
        {number}
      </div>
      {/* Decorative Element */}
      <div 
        className="absolute -bottom-4 -right-4 w-32 h-32 rounded-2xl -z-10"
        style={{ 
          background: `${color}20`,
          border: `2px dashed ${color}40`
        }}
      />
    </div>
    
    {/* Content Side */}
    <div className={isReversed ? 'lg:order-1' : ''}>
      <div className="flex items-center gap-3 mb-4">
        <div 
          className="p-4 rounded-2xl"
          style={{ background: `${color}15` }}
        >
          <Icon size={32} style={{ color: color }} />
        </div>
        <span 
          className="text-sm font-bold uppercase tracking-wider px-3 py-1 rounded-full"
          style={{ background: `${color}20`, color: color }}
        >
          Step {number}
        </span>
      </div>
      
      <h2 
        className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6"
        style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}
      >
        {title}
      </h2>
      
      <p 
        className="mb-8 text-lg"
        style={{ color: "var(--text-200)", lineHeight: "1.8" }}
      >
        {description}
      </p>
      
      <ul className="space-y-4">
        {features.map((feature, index) => (
          <li key={index} className="flex items-start gap-3">
            <div 
              className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
              style={{ background: `${color}20` }}
            >
              <CheckCircle size={16} style={{ color: color }} />
            </div>
            <span style={{ color: "var(--text-200)" }}>{feature}</span>
          </li>
        ))}
      </ul>
    </div>
  </div>
);

// Stat Card Component
const StatCard = ({ value, label, icon: Icon }) => (
  <div 
    className="card p-6 text-center"
    style={{ background: "var(--background-200)" }}
  >
    <div 
      className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4"
      style={{ background: "var(--primary-100)" }}
    >
      <Icon size={28} style={{ color: "white" }} />
    </div>
    <div 
      className="text-3xl md:text-4xl font-bold mb-2"
      style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}
    >
      {value}
    </div>
    <p style={{ color: "var(--text-300)" }}>{label}</p>
  </div>
);

// Main 3-Steps Page Component
const ThreeStepsPage = () => {
  const steps = [
    {
      number: "1",
      title: "Capture Everything",
      icon: Camera,
      color: "#0038a8",
      image: "https://www.machinerymarketed.com/app-appraisal.jpg",
      description: "Start with a guided digital appraisal. Your sales team captures all the essential information right at the point of trade — no more lost paperwork or forgotten details.",
      features: [
        "High-quality photos with guided prompts",
        "Machine specifications and hour readings",
        "Condition assessment with structured fields",
        "Customer expectations and trade details",
        "Works offline, syncs when connected"
      ]
    },
    {
      number: "2",
      title: "Set The Value",
      icon: Calculator,
      color: "#0049db",
      image: "https://www.machinerymarketed.com/app-valuation.jpg",
      description: "Generate instant, professional PDF reports for your valuation team. AI-assisted market analysis helps you make confident decisions faster than ever before.",
      features: [
        "One-click PDF report generation",
        "AI-powered comparable machine analysis",
        "Margin and reconditioning calculators",
        "Manager approval workflow",
        "Consistent, auditable valuation process"
      ]
    },
    {
      number: "3",
      title: "Go To Market",
      icon: Megaphone,
      color: "#0f5fff",
      image: "https://www.machinerymarketed.com/app-machine-details.jpg",
      description: "Transform appraisal data into professional listings instantly. Push to your website, marketplaces, social media, and buyer databases — all with a single click.",
      features: [
        "Professional listing templates",
        "Multi-channel distribution",
        "Social media integration",
        "Email campaign connectivity",
        "Real-time listing analytics"
      ]
    }
  ];

  const stats = [
    { value: "10-15", label: "Minutes to Appraise", icon: Clock },
    { value: "1 Click", label: "To Market", icon: Zap },
    { value: "3x", label: "Faster Time-to-Sale", icon: TrendingUp },
    { value: "100%", label: "Secure & Backed Up", icon: Shield }
  ];

  return (
    <div style={{ background: "var(--background-100)", minHeight: "100vh" }}>
      <Navigation />
      
      <main className="pt-24">
        {/* Hero Section */}
        <section className="relative min-h-[70vh] flex items-center">
          <div className="absolute inset-0 z-0">
            <img 
              src="https://www.machinerymarketed.com/hero_excavator.jpg" 
              alt="Construction machinery"
              className="w-full h-full object-cover"
              style={{ opacity: 0.2 }}
              loading="eager"
            />
            <div 
              className="absolute inset-0"
              style={{ 
                background: "linear-gradient(to bottom, rgba(0,12,37,0.8) 0%, var(--background-100) 100%)" 
              }}
            />
          </div>
          
          <div className="container mx-auto px-4 lg:px-8 relative z-10 pt-16">
            <div className="max-w-4xl mx-auto text-center">
              <div 
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold mb-6"
                style={{ background: "rgba(15, 95, 255, 0.2)", border: "1px solid rgba(15, 95, 255, 0.3)" }}
              >
                <Sparkles size={18} style={{ color: "var(--primary-300)" }} />
                <span style={{ color: "var(--text-100)" }}>Simple Process</span>
              </div>
              <h1 
                className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-8 px-2"
                style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)", wordWrap: "break-word" }}
              >
                Three Steps. <span className="text-gradient">Zero Hassle.</span>
              </h1>
              <p 
                className="text-xl md:text-2xl max-w-3xl mx-auto mb-12"
                style={{ color: "var(--text-200)" }}
              >
                MachineryMarketed streamlines your entire trade-in workflow into three simple stages. 
                From first contact to market-ready listing in minutes, not days.
              </p>
              
              {/* Stats Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
                {stats.map((stat, index) => (
                  <StatCard key={index} {...stat} />
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Steps Detail Section */}
        <section className="py-20">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="max-w-6xl mx-auto">
              {steps.map((step, index) => (
                <StepCard 
                  key={index} 
                  {...step} 
                  isReversed={index % 2 === 1}
                />
              ))}
            </div>
          </div>
        </section>

        {/* Process Flow Section */}
        <section className="py-20" style={{ background: "var(--background-200)" }}>
          <div className="container mx-auto px-4 lg:px-8">
            <div className="text-center mb-16">
              <h2 
                className="text-3xl md:text-4xl font-bold mb-4"
                style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}
              >
                The Complete Flow
              </h2>
              <p style={{ color: "var(--text-200)" }}>
                See how the three stages connect seamlessly
              </p>
            </div>

            <div className="max-w-5xl mx-auto">
              {/* Timeline */}
              <div className="relative">
                {/* Connection Line */}
                <div 
                  className="absolute left-1/2 top-0 bottom-0 w-0.5 -translate-x-1/2 hidden md:block"
                  style={{ background: "linear-gradient(to bottom, #0038a8, #0049db, #0f5fff)" }}
                />

                {[
                  {
                    stage: "Customer Arrives",
                    desc: "Salesperson opens app and starts new appraisal",
                    time: "0 min"
                  },
                  {
                    stage: "Photo Capture",
                    desc: "Guided photo workflow ensures nothing is missed",
                    time: "3 min"
                  },
                  {
                    stage: "Data Entry",
                    desc: "Quick spec entry with auto-complete fields",
                    time: "7 min"
                  },
                  {
                    stage: "Submit for Valuation",
                    desc: "PDF sent instantly to valuation manager",
                    time: "10 min"
                  },
                  {
                    stage: "Value Approved",
                    desc: "Manager reviews and approves trade value",
                    time: "15 min"
                  },
                  {
                    stage: "Deal Closed",
                    desc: "Trade-in accepted, machine ready to market",
                    time: "20 min"
                  },
                  {
                    stage: "Listing Published",
                    desc: "One-click publish to all channels",
                    time: "25 min"
                  },
                  {
                    stage: "Buyers Notified",
                    desc: "Your machine is now visible to thousands",
                    time: "30 min"
                  }
                ].map((item, index) => (
                  <div 
                    key={index} 
                    className={`flex items-center gap-8 mb-8 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}
                  >
                    <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                      <h4 
                        className="text-lg font-bold mb-1"
                        style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}
                      >
                        {item.stage}
                      </h4>
                      <p style={{ color: "var(--text-300)" }}>{item.desc}</p>
                    </div>
                    <div 
                      className="w-16 h-16 rounded-full flex items-center justify-center flex-shrink-0 z-10"
                      style={{ 
                        background: index < 3 ? '#0038a8' : index < 6 ? '#0049db' : '#0f5fff',
                        boxShadow: `0 0 20px ${index < 3 ? '#0038a8' : index < 6 ? '#0049db' : '#0f5fff'}40`
                      }}
                    >
                      <span className="text-white font-bold text-sm">{item.time}</span>
                    </div>
                    <div className="flex-1 hidden md:block" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="text-center mb-16">
              <h2 
                className="text-3xl md:text-4xl font-bold mb-4"
                style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}
              >
                Why This Works
              </h2>
              <p style={{ color: "var(--text-200)" }}>
                Built for dealerships that want to move metal faster
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {[
                {
                  title: "No Double Handling",
                  desc: "Enter data once, use it everywhere. Photos, specs, and descriptions flow seamlessly from appraisal to listing.",
                  color: "#0038a8"
                },
                {
                  title: "Consistent Quality",
                  desc: "Guided workflows ensure every appraisal meets your standards. No more incomplete submissions or missing photos.",
                  color: "#0049db"
                },
                {
                  title: "Faster Decisions",
                  desc: "Valuation managers get everything they need in one PDF. Approve trades in minutes, not hours or days.",
                  color: "#0f5fff"
                },
                {
                  title: "Immediate Marketing",
                  desc: "Don't let machines sit. Get them in front of buyers while they're still fresh and the market is hot.",
                  color: "#0038a8"
                },
                {
                  title: "Works Anywhere",
                  desc: "In the yard, on the lot, or at a customer's farm. Mobile-first design means your team can work anywhere.",
                  color: "#0049db"
                },
                {
                  title: "AI-Powered",
                  desc: "Smart suggestions for specs, market comparables, and optimal pricing help your team make better decisions.",
                  color: "#0f5fff"
                }
              ].map((benefit, index) => (
                <div 
                  key={index}
                  className="card p-8"
                  style={{ 
                    background: "var(--background-200)",
                    borderTop: `3px solid ${benefit.color}`
                  }}
                >
                  <h3 
                    className="text-xl font-bold mb-3"
                    style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}
                  >
                    {benefit.title}
                  </h3>
                  <p style={{ color: "var(--text-200)", lineHeight: "1.7" }}>
                    {benefit.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20" style={{ background: "var(--background-200)" }}>
          <div className="container mx-auto px-4 lg:px-8">
            <div 
              className="relative rounded-2xl overflow-hidden max-w-4xl mx-auto"
            >
              {/* Background */}
              <div 
                className="absolute inset-0 z-0"
                style={{
                  background: "linear-gradient(135deg, var(--primary-100) 0%, var(--primary-300) 100%)"
                }}
              />
              
              {/* Content */}
              <div className="relative z-10 p-12 text-center">
                <h2 
                  className="text-3xl md:text-4xl font-bold mb-6"
                  style={{ fontFamily: "'Smooch Sans', sans-serif", color: "white" }}
                >
                  Ready to Streamline Your Process?
                </h2>
                <p className="mb-8 text-lg" style={{ color: "rgba(255,255,255,0.9)" }}>
                  See how the three-step workflow can transform your dealership's trade-in process.
                </p>
                <div className="flex flex-wrap justify-center gap-4">
                  <Link 
                    to="/#contact" 
                    className="inline-flex items-center gap-2 px-8 py-4 rounded-lg font-semibold transition-all"
                    style={{ 
                      background: "white", 
                      color: "var(--primary-100)"
                    }}
                  >
                    Request a Demo <ArrowRight size={20} />
                  </Link>
                  <Link 
                    to="/how-it-works" 
                    className="inline-flex items-center gap-2 px-8 py-4 rounded-lg font-semibold transition-all"
                    style={{ 
                      background: "transparent", 
                      border: "2px solid white",
                      color: "white"
                    }}
                  >
                    Learn More
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="py-12" style={{ background: "var(--background-200)", borderTop: "1px solid rgba(176, 176, 176, 0.1)" }}>
        <div className="container mx-auto px-4 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img 
                src="https://www.machinerymarketed.com/app-logo.png" 
                alt="MachineryMarketed Logo" 
                className="h-10 w-10"
              />
              <span className="font-bold text-lg" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
                MACHINERYMARKETED
              </span>
            </div>

            <div className="flex items-center gap-6">
              <Link to="/about" className="footer-link text-sm">About</Link>
              <Link to="/privacy" className="footer-link text-sm">Privacy</Link>
              <Link to="/terms" className="footer-link text-sm">Terms</Link>
              <Link to="/faq" className="footer-link text-sm">FAQ</Link>
            </div>

            <p className="text-sm" style={{ color: "var(--text-300)" }}>
              © 2026 MachineryMarketed. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ThreeStepsPage;
